import service from './request';
import { cachedRequest, paramsKey } from './cache';
import { withPreload } from './preload';

// ─── Types ────────────────────────────────────────────────────────────────────

export type ProductType = "hardware" | "software" | "service";
/** 一级分类标识："all" 或解决方案分类标识（info-security / it-infrastructure / cloud-xinda / ai-business …），由接口数据决定 */
export type L1Id = string;

export interface L1Category {
  id: L1Id;
  name: string;
}

export interface L2Category {
  id: string;
  name: string;
  l1: L1Id;
}

export interface L3Category {
  id: string;
  name: string;
  l2: string;
  l1: L1Id;
}

export interface ProductCategories {
  l1Categories: L1Category[];
  l2Categories: L2Category[];
  l3Categories: L3Category[];
}

export interface ProductVariant {
  name: string;
  model: string;
  specs: string;
  recommended?: boolean;
}

export interface TopBrand {
  name: string;
  logo: string;
  tag: string;
  desc: string;
  website: string;
}

export interface Product {
  id: number;
  category: string;
  type: ProductType | ProductType[];
  tag: string;
  name: string;
  model: string;
  desc: string;
  specs: string[];
  highlights: string[];
  image: string;
  features: { icon: string; title: string; desc: string }[];
  advantages: { icon: string; title: string; desc: string }[];
  variants: ProductVariant[];
  relatedSolution: string;
  useCases: string[];
  scenarioTags: string[];
  topBrands: TopBrand[];
  architectureImage: string;
  architectureDesc: string;
}

export interface ArchitectureInfo {
  title: string;
  image: string;
  desc: string;
}

// ─── API calls ────────────────────────────────────────────────────────────────

export function getProductCategories(): Promise<ProductCategories> {
  return cachedRequest('products/categories', () => service.get('/api/front/products/categories')).then((res: any) => {
    const l1Raw = res.l1Categories || [];
    const l2Raw = res.l2Categories || [];
    const l3Raw = res.l3Categories || [];
    return {
      l1Categories: [
        { id: "all" as L1Id, name: "全部产品" },
        ...l1Raw.map((c: any) => ({ id: c.categoryId || c.id, name: c.name })),
      ],
      l2Categories: l2Raw.map((c: any) => ({ id: c.categoryId || c.id, name: c.name, l1: c.l1Id || c.l1 })),
      l3Categories: l3Raw.map((c: any) => ({ id: c.categoryId || c.id, name: c.name, l2: c.l2Id || c.l2, l1: c.l1Id || c.l1 })),
    };
  });
}

export function getProducts(params?: Record<string, unknown>): Promise<Product[]> {
  // 默认不分页，取全量数据用于前端筛选
  const mergedParams = { pageNum: 1, pageSize: 9999, ...params };
  return cachedRequest(
    'products/list?' + paramsKey(mergedParams),
    () => service.get('/api/front/products', { params: mergedParams }),
  ).then((res: any) => {
    const rows = res.rows ?? res.data ?? (Array.isArray(res) ? res : []);
    return rows.map((p: any) => ({
      ...p,
      desc: p.desc ?? p.description ?? "",
      specs: p.specs ?? [],
      highlights: p.highlights ?? [],
      features: p.features ?? [],
      advantages: p.advantages ?? [],
      variants: p.variants ?? [],
      useCases: p.useCases ?? p.applicableScenes ?? [],
      scenarioTags: p.scenarioTags ?? [],
      topBrands: p.topBrands ?? [],
    }));
  });
}

function fetchProductById(id: number): Promise<Product> {
  return service.get('/api/front/products/' + id).then((res: any) => {
    const p = res.data ?? res;
    return {
      ...p,
      desc: p.desc ?? p.description ?? "",
      specs: (p.specs ?? []).map((s: any) => typeof s === "string" ? s : (s.specKey ?? s.key ?? "")),
      highlights: (p.highlights ?? []).map((h: any) => typeof h === "string" ? h : h.text ?? ""),
      features: (p.detailFeatures ?? p.features ?? []).map((f: any) => typeof f === "string" ? { icon: "", title: f, desc: "" } : { icon: f.icon ?? "", title: f.name ?? f.title ?? "", desc: f.desc ?? f.description ?? "" }),
      advantages: (p.advantages ?? []).map((a: any) => ({ icon: a.icon ?? "", title: a.title ?? a.name ?? "", desc: a.desc ?? a.description ?? "" })),
      useCases: p.useCases ?? p.applicableScenes ?? [],
      scenes: (p.scenes ?? []).map((s: any) => typeof s === "string" ? s : s.text ?? ""),
      scenarioTags: p.scenarioTags ?? [],
      variants: (p.variants ?? []).map((v: any) => ({ name: v.name ?? "", model: v.model ?? "", specs: v.specs ?? "", recommended: !!v.recommended })),
      versions: (p.versions ?? []).map((v: any) => ({ name: v.name ?? "", model: v.model ?? "", desc: v.desc ?? v.description ?? "", recommended: !!v.recommended, badge: v.badge ?? "" })),
      versionFeatures: (p.versionFeatures ?? []).map((vf: any) => ({ label: vf.label ?? "", values: vf.values ?? vf.valuesList ?? [] })),
      topBrands: p.topBrands ?? [],
    };
  });
}

// 直连调用（不走 cachedRequest），接入 preload 适配器：首屏命中内嵌状态直接返回
// 并标记消耗，后续交互（同一 id 再次调用）走真实请求。
export const getProductById = withPreload(
  'GET',
  (id: number) => '/api/front/products/' + id,
  fetchProductById,
);

export function getArchitecture(categoryId: string): Promise<ArchitectureInfo> {
  return service.get('/api/front/products/architecture/' + categoryId);
}

export function getBrandWebsites(): Promise<Record<string, string>> {
  return service.get('/api/front/products/brand-websites');
}
